/**
 * Unified Status Color Palette
 * 
 * Single source of truth for all status-related colors across the application.
 * Used by Dashboard, Relations Tab, and any other component displaying contact statuses.
 */

// Status color definitions
export const STATUS_COLORS = {
  // Lead - Orange (potential customer, abandoned checkout)
  lead: {
    bg: 'bg-orange-50',
    bgSolid: 'bg-orange-100',
    bgGradientFrom: 'from-orange-50',
    bgGradientTo: 'to-amber-50',
    border: 'border-orange-200',
    text: 'text-orange-700',
    textDark: 'text-orange-800',
    textLight: 'text-orange-600',
    icon: 'text-orange-600',
    dot: 'bg-orange-500',
    hex: '#f97316', // orange-500
  },

  // Returning - Darker Orange (customer who started new checkout)
  returning: {
    bg: 'bg-orange-50',
    bgSolid: 'bg-orange-100',
    bgGradientFrom: 'from-orange-50',
    bgGradientTo: 'to-amber-50',
    border: 'border-orange-300',
    text: 'text-orange-700',
    textDark: 'text-orange-800',
    textLight: 'text-orange-600',
    icon: 'text-orange-600',
    dot: 'bg-orange-500',
    hex: '#ea580c', // orange-600
  },

  // Customer - Emerald/Green (paying customer)
  customer: {
    bg: 'bg-emerald-50',
    bgSolid: 'bg-emerald-100',
    bgGradientFrom: 'from-emerald-50',
    bgGradientTo: 'to-green-50',
    border: 'border-emerald-200',
    text: 'text-emerald-700',
    textDark: 'text-emerald-800',
    textLight: 'text-emerald-600',
    icon: 'text-emerald-600',
    dot: 'bg-emerald-500',
    hex: '#10b981', // emerald-500
  },

  // Ghost - Gray (cold/lost lead, 7+ days inactive)
  ghost: {
    bg: 'bg-gray-50',
    bgSolid: 'bg-gray-100',
    bgGradientFrom: 'from-gray-50',
    bgGradientTo: 'to-slate-50',
    border: 'border-gray-200',
    text: 'text-gray-500',
    textDark: 'text-gray-600',
    textLight: 'text-gray-400',
    icon: 'text-gray-400',
    dot: 'bg-gray-400',
    hex: '#6b7280', // gray-500
  },

  // Waiver - Purple (only signed waiver, no booking)
  waiver: {
    bg: 'bg-purple-50',
    bgSolid: 'bg-purple-100',
    bgGradientFrom: 'from-purple-50',
    bgGradientTo: 'to-violet-50',
    border: 'border-purple-200',
    text: 'text-purple-700',
    textDark: 'text-purple-800',
    textLight: 'text-purple-600',
    icon: 'text-purple-600',
    dot: 'bg-purple-500',
    hex: '#8b5cf6', // purple-500
  },
};

/**
 * Get status badge configuration for Relations tab
 * @param {Object} contact - Contact object with status and flags
 * @returns {Object} Badge configuration with label and className
 */
export const getStatusBadge = (contact) => {
  const status = contact?.status;
  const isReturning = contact?.isReturningCustomer || status === 'recent_lead';

  if (status === 'ghost') {
    return {
      label: 'Ghost',
      className: `${STATUS_COLORS.ghost.bg} ${STATUS_COLORS.ghost.text} ${STATUS_COLORS.ghost.border}`,
      color: STATUS_COLORS.ghost,
    };
  }

  if (status === 'returning' || isReturning || 
      (contact?.hasRecentLead && status === 'lead')) {
    return {
      label: 'Returning',
      className: `${STATUS_COLORS.returning.bg} ${STATUS_COLORS.returning.text} ${STATUS_COLORS.returning.border}`,
      color: STATUS_COLORS.returning,
    };
  }

  if (status === 'customer') {
    return {
      label: 'Customer',
      className: `${STATUS_COLORS.customer.bg} ${STATUS_COLORS.customer.text} ${STATUS_COLORS.customer.border}`,
      color: STATUS_COLORS.customer,
    };
  }

  if (status === 'waiver') {
    return {
      label: 'Waiver',
      className: `${STATUS_COLORS.waiver.bg} ${STATUS_COLORS.waiver.text} ${STATUS_COLORS.waiver.border}`,
      color: STATUS_COLORS.waiver,
    };
  }

  // Default: Lead
  return {
    label: 'Lead',
    className: `${STATUS_COLORS.lead.bg} ${STATUS_COLORS.lead.text} ${STATUS_COLORS.lead.border}`,
    color: STATUS_COLORS.lead,
  };
};

/**
 * Get avatar background color based on status
 * @param {string} status - Contact status
 * @returns {string} Tailwind class for avatar background
 */
export const getAvatarBgColor = (status) => {
  if (status === 'customer') return STATUS_COLORS.customer.bgSolid;
  if (status === 'lead' || status === 'recent_lead') return STATUS_COLORS.lead.bgSolid;
  if (status === 'ghost') return STATUS_COLORS.ghost.bgSolid;
  if (status === 'waiver') return STATUS_COLORS.waiver.bgSolid;
  return STATUS_COLORS.lead.bgSolid;
};

/**
 * Get avatar text color based on status
 * @param {string} status - Contact status
 * @returns {string} Tailwind class for avatar text
 */
export const getAvatarTextColor = (status) => {
  if (status === 'customer') return STATUS_COLORS.customer.textLight;
  if (status === 'lead' || status === 'recent_lead') return STATUS_COLORS.lead.textLight;
  if (status === 'ghost') return STATUS_COLORS.ghost.textLight;
  if (status === 'waiver') return STATUS_COLORS.waiver.textLight;
  return STATUS_COLORS.lead.textLight;
};

export default STATUS_COLORS;
